import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { DateTime } from 'luxon';

export const ShiftsReport = ({
    processedShifts,
    showUnderstaffed,
    showOverstaffed,
    shiftsPage,
    shiftsRowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    getPatrollerName,
    getRoleName
}) => {
    // Ensure processedShifts is an array
    const safeProcessedShifts = processedShifts || [];

    // Filter shifts based on staffing toggles
    const filteredShifts = React.useMemo(() => {
        return safeProcessedShifts.filter(shift => {
            const totalSlots = shift.totalSlots;
            const assignedCount = shift.assignedCount;
            const isUnderstaffed = assignedCount < totalSlots;
            const isOverstaffed = assignedCount > totalSlots;

            // If no filters active, show all
            if (!showUnderstaffed && !showOverstaffed) return true;

            // If both filters active, show understaffed OR overstaffed
            if (showUnderstaffed && showOverstaffed) {
                return isUnderstaffed || isOverstaffed;
            }

            // If only understaffed filter active
            if (showUnderstaffed) return isUnderstaffed;

            // If only overstaffed filter active
            if (showOverstaffed) return isOverstaffed;

            return true;
        });
    }, [ safeProcessedShifts, showUnderstaffed, showOverstaffed ]);

    // Group shifts by day
    const shiftsByDay = React.useMemo(() => {
        const shiftsByDay = {};

        filteredShifts.forEach(shift => {
            const dt = shift.dateTime;
            const dayKey = dt.toFormat('yyyy-MM-dd');
            const dayLabel = dt.toFormat('EEEE, MMMM dd, yyyy');

            if (!shiftsByDay[ dayKey ]) {
                shiftsByDay[ dayKey ] = {
                    label: dayLabel,
                    shifts: []
                };
            }
            shiftsByDay[ dayKey ].shifts.push(shift);
        });

        return shiftsByDay;
    }, [ filteredShifts ]);

    const dayEntries = React.useMemo(() => Object.entries(shiftsByDay), [ shiftsByDay ]);

    const paginatedDayEntries = React.useMemo(() =>
        dayEntries.slice(
            shiftsPage * shiftsRowsPerPage,
            shiftsPage * shiftsRowsPerPage + shiftsRowsPerPage
        ),
        [ dayEntries, shiftsPage, shiftsRowsPerPage ]
    );

    // Calculate summary statistics
    const totalShifts = filteredShifts.length;
    const understaffedShifts = filteredShifts.filter(s => s.assignedCount < s.totalSlots).length;
    const overstaffedShifts = filteredShifts.filter(s => s.assignedCount > s.totalSlots).length;
    const totalSlots = filteredShifts.reduce((sum, s) => sum + s.totalSlots, 0);
    const totalAssigned = filteredShifts.reduce((sum, s) => sum + s.assignedCount, 0);

    return (
        <Box>
            {/* Summary Cards */ }
            <Box sx={ { display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' } }>
                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color="primary">{ totalShifts }</Typography>
                        <Typography variant="body2" color="text.secondary">Total Shifts</Typography>
                    </CardContent>
                </Card>

                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color="error.main">{ understaffedShifts }</Typography>
                        <Typography variant="body2" color="text.secondary">Understaffed</Typography>
                    </CardContent>
                </Card>

                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color="warning.main">{ overstaffedShifts }</Typography>
                        <Typography variant="body2" color="text.secondary">Overstaffed</Typography>
                    </CardContent>
                </Card>

                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color="success.main">{ totalAssigned }/{ totalSlots }</Typography>
                        <Typography variant="body2" color="text.secondary">Total Filled</Typography>
                    </CardContent>
                </Card>
            </Box>

            { paginatedDayEntries.map(([ dayKey, dayData ]) => {
                const dayDate = DateTime.fromFormat(dayKey, 'yyyy-MM-dd');
                const isSaturday = dayDate.weekday === 6;

                const hasOverUnderstaffedShifts = dayData.shifts.some(shift => {
                    const totalSlots = shift.totalSlots;
                    const assignedCount = shift.assignedCount;

                    return assignedCount !== totalSlots;
                });

                return (
                    <Accordion key={ dayKey }>
                        <AccordionSummary
                            expandIcon={ <ExpandMoreIcon /> }
                            sx={ isSaturday ? { backgroundColor: '#454545ff' } : {} }
                        >
                            <Typography variant="h6" sx={ hasOverUnderstaffedShifts ? { color: 'warning.light' } : {} }>
                                { dayData.label } ({ dayData.shifts.length } shift{ dayData.shifts.length !== 1 ? 's' : '' })
                            </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                            <TableContainer component={ Paper }>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant="body1">Shift Name</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body1">Time</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body1">Slots</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body1">Assigned Patrollers</Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        { dayData.shifts.map((shift) => {
                                            const totalSlots = shift.totalSlots;
                                            const assignedCount = shift.assignedCount;
                                            const slotsColor = totalSlots > assignedCount ? 'red' : totalSlots === assignedCount ? 'green' : 'orange';
                                            const isSaturday = shift.isSaturday;

                                            return (
                                                <TableRow
                                                    key={ shift.id }
                                                    sx={ isSaturday ? {} : {} }
                                                >
                                                    <TableCell>
                                                        <Typography variant="body1">{ shift.name }</Typography>
                                                    </TableCell>

                                                    <TableCell>
                                                        <Typography variant="body1">{ shift.dateTime.toFormat('hh:mm a') }</Typography>
                                                    </TableCell>

                                                    <TableCell sx={ { color: slotsColor, fontWeight: 'bold' } }>
                                                        <Typography variant="body1">{ assignedCount } / { totalSlots }</Typography>
                                                    </TableCell>

                                                    <TableCell>
                                                        { shift.patrollers?.map((p, index) => (
                                                            <Typography key={ index }>
                                                                { getPatrollerName(p.id) } ({ getRoleName(p.role, shift.roles) })
                                                            </Typography>
                                                        )) || 'No assignments' }
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }) }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                );
            }) }
            <TablePagination
                component="div"
                count={ dayEntries.length }
                page={ shiftsPage }
                onPageChange={ onPageChange }
                rowsPerPage={ shiftsRowsPerPage }
                onRowsPerPageChange={ onRowsPerPageChange }
                rowsPerPageOptions={ [ 5, 10, 25, 50 ] }
            />
        </Box>
    );
};